import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  useTheme,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { useAuth } from '../services/auth';

function Users() {
  const theme = useTheme();

  // Helper function to format UTC timestamp to Manila time using manual offset
  const formatToManila = (utcString, dateFormat = {}, timeFormat = {}) => {
    if (!utcString) return null;

    // Parse the UTC timestamp
    const utcDate = new Date(utcString);
    if (isNaN(utcDate.getTime())) {
      return null;
    }

    // Convert UTC to Manila time (UTC+8) by adding 8 hours
    const manilaOffset = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
    const manilaDate = new Date(utcDate.getTime() + manilaOffset);

    // Format the Manila time
    const dateOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      ...dateFormat
    };

    const timeOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      ...timeFormat
    };

    return {
      date: manilaDate.toLocaleDateString('en-US', dateOptions),
      time: manilaDate.toLocaleTimeString('en-US', timeOptions)
    };
  };
  const {
    user: currentUser,
    registerUser,
    getUsers,
    updateUserRole,
    deleteUser,
    bulkDeleteProductInventory,
    bulkDeleteQuaipoLookup,
    canManageUsers
  } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDataDialogOpen, setDeleteDataDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    role: 'employee',
  });
  const [deleteDataSelections, setDeleteDataSelections] = useState({
    productInventory: false,
    quaipoLookup: false,
  });
  const [deleteDataLoading, setDeleteDataLoading] = useState(false);
  const [deleteDataConfirmation, setDeleteDataConfirmation] = useState('');

  useEffect(() => {
    if (canManageUsers()) {
      fetchUsers();
    }
  }, []); // Remove canManageUsers from dependencies to avoid re-renders

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const usersData = await getUsers();
      setUsers(usersData || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    try {
      setError('');
      await registerUser(newUser);
      setSuccess('User created successfully');
      setAddDialogOpen(false);
      resetNewUserForm();
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to create user');
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;

    try {
      setError('');
      await updateUserRole(selectedUser.Id || selectedUser.ID || selectedUser.id, selectedUser.Role);
      setSuccess('User role updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update user role');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setError('');
      await deleteUser(selectedUser.Id || selectedUser.ID || selectedUser.id);
      setSuccess('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    }
  };

  const handleDeleteDataOpen = () => {
    setDeleteDataDialogOpen(true);
    setDeleteDataSelections({ productInventory: false, quaipoLookup: false });
  };

  const handleDeleteDataClose = () => {
    setDeleteDataDialogOpen(false);
    setDeleteDataSelections({ productInventory: false, quaipoLookup: false });
  };

  const handleDeleteDataSelectionChange = (key) => {
    setDeleteDataSelections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDeleteDataConfirm = async () => {
    const { productInventory, quaipoLookup } = deleteDataSelections;

    if (!productInventory && !quaipoLookup) {
      setError('Please select at least one data type to delete');
      return;
    }

    try {
      setError('');
      setDeleteDataLoading(true);

      const results = [];

      if (productInventory) {
        const result = await bulkDeleteProductInventory('DELETE_ALL_PRODUCT_INVENTORY');
        results.push({ type: 'Product & Inventory Data', deleted: result.DeletedCount || 0 });
      }

      if (quaipoLookup) {
        const result = await bulkDeleteQuaipoLookup('DELETE_ALL_QUAIPO_LOOKUP');
        results.push({ type: 'Supplier Parts Lookup Data', deleted: result.DeletedCount || 0 });
      }

      setSuccess(`Deleted: ${results.map(r => `${r.type} (${r.deleted} records)`).join(', ')}`);
      setDeleteDataDialogOpen(false);
      setDeleteDataSelections({ productInventory: false, quaipoLookup: false });
      fetchUsers(); // Refresh user list to ensure consistent state
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Failed to delete data');
    } finally {
      setDeleteDataLoading(false);
    }
  };

  const resetNewUserForm = () => {
    setNewUser({
      username: '',
      email: '',
      password: '',
      fullName: '',
      role: 'employee',
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return theme.palette.error.main;
      case 'manager':
        return theme.palette.warning.main;
      case 'employee':
        return theme.palette.info.main;
      case 'viewer':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <SecurityIcon />;
      case 'manager':
        return <PersonIcon />;
      case 'employee':
        return <PersonIcon />;
      case 'viewer':
        return <PersonIcon />;
      default:
        return <PersonIcon />;
    }
  };

  
  if (!canManageUsers()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access user management.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            User Management
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Manage user accounts and roles
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton
            onClick={fetchUsers}
            disabled={loading}
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteDataOpen}
            disabled={loading}
            sx={{
              px: 3,
              py: 1.5,
            }}
          >
            Delete Data
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
              color: '#000000',
              px: 3,
              py: 1.5,
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Role Information Card */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, rgba(0, 191, 255, 0.05) 0%, rgba(0, 128, 255, 0.05) 100%)', border: '1px solid rgba(0, 191, 255, 0.2)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
            Role Permissions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: 1, border: '1px solid rgba(244, 67, 54, 0.3)', minHeight: 120 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 1 }}>
                  Admin
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Full system access, user management, all CRUD operations
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 1, border: '1px solid rgba(255, 152, 0, 0.3)', minHeight: 120 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#f57c00', mb: 1 }}>
                  Manager
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Create and update operations, no deletions or user management
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)', borderRadius: 1, border: '1px solid rgba(33, 150, 243, 0.3)', minHeight: 120 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                  Employee
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Create and update operations, no deletions or user management
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1, border: '1px solid rgba(76, 175, 80, 0.3)', minHeight: 120 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#388e3c', mb: 1 }}>
                  Viewer
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Read-only access, no modifications allowed
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Users Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Loading users...</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.Id || user.ID || user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              backgroundColor: getRoleColor(user.Role),
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                            }}
                          >
                            {getRoleIcon(user.Role)}
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {user.FullName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              @{user.Username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.Role?.toUpperCase()}
                          size="small"
                          sx={{
                            backgroundColor: `${getRoleColor(user.Role)}20`,
                            color: getRoleColor(user.Role),
                            fontWeight: 600,
                            border: `1px solid ${getRoleColor(user.Role)}60`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.Email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatToManila(user.LastLogin)?.date && formatToManila(user.LastLogin)?.time
                            ? `${formatToManila(user.LastLogin).date} ${formatToManila(user.LastLogin).time}`
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatToManila(user.CreatedAt)?.date && formatToManila(user.CreatedAt)?.time
                            ? `${formatToManila(user.CreatedAt).date} ${formatToManila(user.CreatedAt).time}`
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedUser(user);
                            setEditDialogOpen(true);
                          }}
                          disabled={false}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          disabled={false}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={newUser.fullName}
                onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                helperText="Minimum 6 characters"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newUser.role}
                  label="Role"
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <MenuItem value="admin">
                    <Box>
                      <Typography>Admin</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Full system access, user management, all operations
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="manager">
                    <Box>
                      <Typography>Manager</Typography>
                      <Typography variant="caption" color="textSecondary">
                        All CRUD operations, no user management
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="employee">
                    <Box>
                      <Typography>Employee</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Create and update operations, no deletions or user management
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="viewer">
                    <Box>
                      <Typography>Viewer</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Read-only access, no modifications allowed
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddDialogOpen(false);
              resetNewUserForm();
            }}
            sx={{
              color: '#000000',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={!newUser.username || !newUser.email || !newUser.password || !newUser.fullName}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUser(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              User: {selectedUser?.FullName} (@{selectedUser?.Username})
            </Typography>
            <FormControl fullWidth sx={{ mt: 3 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedUser?.Role || ''}
                label="Role"
                onChange={(e) => setSelectedUser(prev => ({ ...prev, Role: e.target.value }))}
              >
                <MenuItem value="admin">
                  <Box>
                    <Typography>Admin</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Full system access, user management, all operations
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="manager">
                  <Box>
                    <Typography>Manager</Typography>
                    <Typography variant="caption" color="textSecondary">
                      All CRUD operations, no user management
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="employee">
                  <Box>
                    <Typography>Employee</Typography>
                    <Typography variant="caption" color="textSecondary">
                      All CRUD operations, no user management
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="viewer">
                  <Box>
                    <Typography>Viewer</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Read-only access, no modifications allowed
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setSelectedUser(null);
            }}
            variant="contained"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateRole}
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedUser(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.FullName}" (@{selectedUser?.Username})?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedUser(null);
            }}
            variant="contained"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Data Confirmation Dialog */}
      <Dialog
        open={deleteDataDialogOpen}
        onClose={handleDeleteDataClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteForeverIcon sx={{ color: 'error.main' }} />
            <Typography variant="h6" component="span">
              Delete Data
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            <strong>Warning:</strong> This action will permanently delete data and cannot be undone.
            Please select the data type(s) you want to delete:
          </Typography>

          <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 2, mb: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Destructive Action</AlertTitle>
              This will delete ALL data of the selected type(s) across the entire system.
            </Alert>
            <Typography variant="body2" color="textSecondary">
              Type <strong>"DELETE DATA"</strong> in the confirmation field below to proceed.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select data to delete:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteDataSelections.productInventory}
                  onChange={() => handleDeleteDataSelectionChange('productInventory')}
                  disabled={deleteDataLoading}
                  color="error"
                />
              }
              label={
                <Box sx={{ pl: 1 }}>
                  <Typography variant="body1" color="error.main" gutterBottom>
                    Product and Inventory Data
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ ml: 3 }}>
                    Deletes: Products, Pricing, Stock, Stock Movements, Sales, and related Activity Logs
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteDataSelections.quaipoLookup}
                  onChange={() => handleDeleteDataSelectionChange('quaipoLookup')}
                  disabled={deleteDataLoading}
                  color="error"
                />
              }
              label={
                <Box sx={{ pl: 1 }}>
                  <Typography variant="body1" color="error.main" gutterBottom>
                    Supplier Parts Lookup Data
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ ml: 3 }}>
                    Deletes: QUAIPO Lookup entries and related Activity Logs (LOOKUP type)
                  </Typography>
                </Box>
              }
            />
          </Box>

          <TextField
            fullWidth
            label="Type 'DELETE DATA' to confirm"
            type="text"
            value={deleteDataConfirmation}
            onChange={(e) => setDeleteDataConfirmation(e.target.value)}
            disabled={deleteDataLoading}
            error={deleteDataConfirmation && deleteDataConfirmation !== 'DELETE DATA'}
            helperText={deleteDataConfirmation && deleteDataConfirmation !== 'DELETE DATA'
              ? 'Must type exactly: DELETE DATA'
              : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteDataClose}
            variant="contained"
            color="primary"
            disabled={deleteDataLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteDataConfirm}
            disabled={deleteDataLoading || deleteDataConfirmation !== 'DELETE DATA' ||
              (!deleteDataSelections.productInventory && !deleteDataSelections.quaipoLookup)}
          >
            {deleteDataLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete Data'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;